import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import {
  ChevronLeft, Save, Loader2, Plus, Trash2, Sparkles, Shield, AlertTriangle, Mail, X, Info, ExternalLink,
} from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import usePayPermissions from '../../hooks/usePayPermissions';
import { penaltyRate } from '../../utils/penaltyRate';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { useJobTitles } from '../../hooks/useSettings';
import ProfilePhotoUpload from '../../components/ProfilePhotoUpload';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import {
  PAGE, Dialog, Field, FormRow, Switch, CopyButton, TableSkeleton, ErrorState, avatarColor, money,
} from '../../components/ui';
import {
  GENDER_OPTIONS, genderOption, EMPLOYMENT_TYPES, ExpiryBadge, IconButton, useBeforeUnload, useStableCallback,
  roleLabel, employmentLabel, forgetStaffRecord,
} from './hrKit';

/* ─── Sections ───────────────────────────────────────────────────────────── */

const SECTIONS = [
  { id: 'personal', label: 'Personal' },
  { id: 'contact', label: 'Contact' },
  { id: 'role', label: 'Role' },
  { id: 'pay', label: 'Pay' },
  { id: 'tax', label: 'Tax & bank' },
  { id: 'emergency', label: 'Emergency' },
  { id: 'vehicle', label: 'Vehicle' },
  { id: 'compliance', label: 'Compliance' },
];

const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY = {
  firstName: '', lastName: '', email: '', phone: '', address: '', suburb: '', state: '', postcode: '',
  nationality: '', employeeNumber: '',
  dateOfBirth: '', gender: '', role: 'carer', jobTitle: '', employmentType: '',
  payGroup: '', overrideHourlyRate: '', dailyHoursCap: '', weeklyHoursCap: '', standardDailyHours: '',
  contractorWeekdayRate: '', contractorSaturdayRate: '', contractorSundayRate: '', contractorPhRate: '', contractorSuperApplies: false, gstRegistered: false,
  externalSystemId: '', startDate: '', endDate: '',
  emergencyContactName: '', emergencyContactRelationship: '', emergencyContactPhone: '', emergencyContactEmail: '',
  tfn: '', abn: '', bankBsb: '', bankAccountNumber: '', bankAccountName: '',
  superFundName: '', superMemberNumber: '',
  carRegistration: '', carMake: '', carModel: '', carColor: '', carExpiry: '',
  managerId: '', onHold: false,
};

// Fields the create endpoint does not take. When any is filled on a NEW staff member we save them
// straight after the record is created, so nothing typed on the form is silently dropped.
const AFTER_CREATE = [
  'jobTitle', 'employmentType', 'overrideHourlyRate', 'contractorWeekdayRate', 'contractorSaturdayRate',
  'contractorSundayRate', 'contractorPhRate', 'contractorSuperApplies', 'gstRegistered', 'tfn', 'abn', 'bankBsb',
  'bankAccountNumber', 'bankAccountName', 'superFundName', 'superMemberNumber', 'carRegistration',
  'carMake', 'carModel', 'carColor', 'carExpiry', 'suburb', 'state', 'postcode', 'nationality', 'employeeNumber',
];

const digitsOnly = (v) => String(v || '').replace(/\D+/g, '');
const fmtBsb = (v) => { const d = digitsOnly(v).slice(0, 6); return d.length > 3 ? `${d.slice(0, 3)}-${d.slice(3)}` : d; };
const maskTail = (v, keep = 3) => { const d = String(v || '').replace(/\s+/g, ''); return d ? `${'•'.repeat(Math.max(0, d.length - keep))}${d.slice(-keep)}` : ''; };

/* ─── Small pieces ───────────────────────────────────────────────────────── */

function FormSection({ id, title, description, actions, children }) {
  return (
    <section id={id} aria-labelledby={`${id}-title`} className="scroll-mt-24 rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-3.5">
        <div className="min-w-0">
          <h2 id={`${id}-title`} className="text-[15px] font-semibold text-slate-900">{title}</h2>
          {description && <p className="mt-0.5 text-[13px] text-slate-500">{description}</p>}
        </div>
        {actions}
      </div>
      <div className="space-y-4 p-5">{children}</div>
    </section>
  );
}

function AiTag() {
  return <span className="ml-1.5 rounded bg-slate-100 px-1 py-0.5 text-[10.5px] font-semibold text-slate-600" title="Filled in from the CV">AI</span>;
}

/* ─── Existing-worker compliance (edit mode) ─────────────────────────────── */

const QUAL_STATUS_OPTS = [
  { value: 'not_verified', label: 'Not verified' },
  { value: 'pending', label: 'Pending' },
  { value: 'verified', label: 'Verified' },
  { value: 'expired', label: 'Expired' },
];

function QualificationsEditor({ staffId, initialQuals, masterQuals }) {
  const confirm = useConfirm();
  const [quals, setQuals] = useState(initialQuals || []);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({ qual_definition_id: '', expiryDate: '', verificationStatus: 'not_verified' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setQuals(initialQuals || []); }, [initialQuals]);

  const categories = [...new Set(masterQuals.map((q) => q.category_name).filter(Boolean))];
  const alreadyAdded = new Set(quals.map((q) => q.qual_definition_id).filter(Boolean));
  const selectedDef = masterQuals.find((q) => q.id === addForm.qual_definition_id);

  const add = async () => {
    if (!addForm.qual_definition_id) { setError('Choose a qualification.'); return; }
    if (selectedDef && selectedDef.requires_expiry && !addForm.expiryDate) { setError('This qualification needs an expiry date.'); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/staff/${staffId}/qualifications`, {
        qualificationName: selectedDef ? selectedDef.name : '',
        qual_definition_id: addForm.qual_definition_id,
        expiryDate: addForm.expiryDate || null,
        verificationStatus: addForm.verificationStatus,
        appVisible: true,
      });
      setQuals((q) => [...q, data]);
      setAdding(false);
      setAddForm({ qual_definition_id: '', expiryDate: '', verificationStatus: 'not_verified' });
      toast.success('Qualification added');
    } catch (e) { setError(e.response?.data?.message || "The qualification wasn't added. Try again."); }
    finally { setSaving(false); }
  };

  const remove = async (q) => {
    if (!await confirm(`${q.qualification_name} will be removed from this staff member's record.`, { title: 'Remove qualification?', confirmLabel: 'Remove', danger: true })) return;
    try {
      await api.delete(`/staff/${staffId}/qualifications/${q.id}`);
      setQuals((list) => list.filter((x) => x.id !== q.id));
      toast.success('Qualification removed');
    } catch (e) { toast.error(e.response?.data?.message || "Couldn't remove the qualification. Try again."); }
  };

  const updateStatus = async (q, status) => {
    const prev = q.verification_status;
    setQuals((list) => list.map((x) => (x.id === q.id ? { ...x, verification_status: status } : x)));
    try {
      await api.put(`/staff/${staffId}/qualifications/${q.id}`, {
        verificationStatus: status,
        expiryDate: q.expiry_date ? String(q.expiry_date).slice(0, 10) : null,
        appVisible: q.app_visible !== false,
      });
    } catch (e) {
      setQuals((list) => list.map((x) => (x.id === q.id ? { ...x, verification_status: prev } : x)));
      toast.error(e.response?.data?.message || "The status wasn't saved. Try again.");
    }
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-[13px] font-semibold text-slate-700">Qualifications</h3>
        <button type="button" onClick={() => { setAdding((v) => !v); setError(''); }} className="btn-secondary btn-sm">
          {adding ? <X size={13} aria-hidden="true" /> : <Plus size={13} aria-hidden="true" />}{adding ? 'Cancel' : 'Add'}
        </button>
      </div>
      {adding && (
        <div className="mb-3 space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
          <FormRow cols={3}>
            <Field label="Qualification" required error={error}>
              {(id) => (
                <select id={id} className="input-field" value={addForm.qual_definition_id} onChange={(e) => { setAddForm((f) => ({ ...f, qual_definition_id: e.target.value })); setError(''); }}>
                  <option value="">Choose…</option>
                  {categories.map((cat) => (
                    <optgroup key={cat} label={cat}>
                      {masterQuals.filter((q) => q.category_name === cat && !alreadyAdded.has(q.id)).map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
                    </optgroup>
                  ))}
                  {masterQuals.filter((q) => !q.category_name && !alreadyAdded.has(q.id)).map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
                </select>
              )}
            </Field>
            <Field label="Status">
              {(id) => (
                <select id={id} className="input-field" value={addForm.verificationStatus} onChange={(e) => setAddForm((f) => ({ ...f, verificationStatus: e.target.value }))}>
                  {QUAL_STATUS_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              )}
            </Field>
            <Field label="Expiry date" required={!!(selectedDef && selectedDef.requires_expiry)}>
              {(id) => <input id={id} type="date" className="input-field" value={addForm.expiryDate} onChange={(e) => setAddForm((f) => ({ ...f, expiryDate: e.target.value }))} />}
            </Field>
          </FormRow>
          <button type="button" onClick={add} disabled={saving} className="btn-primary btn-sm">
            {saving ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Plus size={13} aria-hidden="true" />}Add qualification
          </button>
        </div>
      )}
      {quals.length === 0 ? (
        <p className="text-[13px] text-slate-500">No qualifications recorded yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {quals.map((q) => (
            <li key={q.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2">
              <span className="min-w-0 flex-1 text-sm font-medium text-slate-800">{q.qualification_name}</span>
              <ExpiryBadge date={q.expiry_date} />
              <select aria-label={`Status of ${q.qualification_name}`} value={q.verification_status || 'not_verified'} onChange={(e) => updateStatus(q, e.target.value)}
                className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[12.5px] text-slate-700">
                {QUAL_STATUS_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <IconButton label={`Remove ${q.qualification_name}`} icon={Trash2} size={14} danger onClick={() => remove(q)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const CLEARANCE_LABEL = { clear: 'Verified', expired: 'Expired', invalid: 'Number not valid', not_checked: 'Not verified yet', in_progress: 'In progress' };
const CLEARANCE_PILL = { clear: 'badge-green', expired: 'badge-red', invalid: 'badge-red', not_checked: 'badge-gray', in_progress: 'badge-yellow' };

function ScreeningEditor({ staffId, record }) {
  const [num, setNum] = useState(record?.ndis_clearance_number || '');
  const [expiry, setExpiry] = useState(record?.ndis_clearance_expiry ? String(record.ndis_clearance_expiry).slice(0, 10) : '');
  const [st, setSt] = useState(record?.ndis_clearance_state || '');
  const [status, setStatus] = useState(record?.ndis_clearance_status || 'not_checked');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);

  const verify = async () => {
    if (!num.trim()) { setError('Enter the clearance number first.'); return; }
    setVerifying(true); setError('');
    try {
      const { data } = await api.post(`/staff/${staffId}/verify-clearance`, { clearanceNumber: num, clearanceExpiry: expiry || null, clearanceState: st || null });
      setStatus(data.status);
      setResult(data);
      forgetStaffRecord(staffId);
      toast.success(data.message || 'Screening details saved');
    } catch (e) { setError(e.response?.data?.message || "The check didn't run. Try again."); }
    finally { setVerifying(false); }
  };

  return (
    <div id="screening" className="scroll-mt-24">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[13px] font-semibold text-slate-700">NDIS worker screening</h3>
        <span className={CLEARANCE_PILL[status] || 'badge-gray'}>{CLEARANCE_LABEL[status] || status}</span>
      </div>
      <div className="space-y-3 rounded-lg border border-slate-200 p-3">
        <FormRow cols={3}>
          <Field label="Clearance number" error={error}>
            {(id) => <input id={id} className="input-field" value={num} onChange={(e) => { setNum(e.target.value); setError(''); }} placeholder="e.g. 1-ABC2DEF" autoComplete="off" />}
          </Field>
          <Field label="Expiry date">
            {(id) => <input id={id} type="date" className="input-field" value={expiry} onChange={(e) => setExpiry(e.target.value)} />}
          </Field>
          <Field label="State issued">
            {(id) => (
              <select id={id} className="input-field" value={st} onChange={(e) => setSt(e.target.value)}>
                <option value="">—</option>
                {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
          </Field>
        </FormRow>
        {result && (
          <p className={clsx('rounded-lg px-3 py-2 text-[13px]', result.status === 'clear' ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800')} role="status">
            {result.message}
            {result.status === 'clear' && result.daysUntilExpiry != null && result.daysUntilExpiry <= 30 && (
              <span className="mt-1 block font-medium text-amber-800">Expires in {result.daysUntilExpiry} days. Remind them to renew.</span>
            )}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={verify} disabled={verifying} className="btn-secondary btn-sm">
            {verifying ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Shield size={13} aria-hidden="true" />}
            {verifying ? 'Checking…' : 'Check and save'}
          </button>
          <a href="https://www.ndiscommission.gov.au/workers/ndis-worker-screening-check" target="_blank" rel="noreferrer" className="btn-ghost btn-sm">
            NDIS Commission portal<ExternalLink size={12} aria-hidden="true" />
          </a>
        </div>
        <p className="text-[12px] text-slate-500">This checks the number's format and expiry date only. Confirm the clearance on the NDIS Commission portal.</p>
      </div>
    </div>
  );
}

/* ─── Page ───────────────────────────────────────────────────────────────── */

export default function StaffForm() {
  const { id } = useParams();
  const { user: me } = useAuth();
  const { can: canPay } = usePayPermissions();
  // Changing where someone is paid needs Pay Rates: Edit, unless it is your own profile.
  const bankLocked = !!id && !canPay('pay_rates.edit') && !(me && me.id === id);
  const navigate = useNavigate();
  const { hash } = useLocation();
  const confirm = useConfirm();
  const isEdit = !!id;
  const fileRef = useRef();
  const initialRef = useRef(JSON.stringify(EMPTY));

  const [loading, setLoading]         = useState(isEdit);
  const [loadError, setLoadError]     = useState(null);
  const [saving, setSaving]           = useState(false);
  const [record, setRecord]           = useState(null);
  const [payGroupOptions, setPayGroupOptions] = useState([]);
  const [masterQuals, setMasterQuals] = useState([]);
  const [staffList, setStaffList]     = useState([]);
  const [extracting, setExtracting]   = useState(false);
  const [filledFields, setFilledFields] = useState([]);
  const [photoUrl, setPhotoUrl]       = useState(null);
  const [form, setForm]               = useState(EMPTY);
  const [errors, setErrors]           = useState({});
  const [saveError, setSaveError]     = useState('');
  const [submitted, setSubmitted]     = useState(false);
  const [tfnFocus, setTfnFocus]       = useState(false);
  const [genderSelf, setGenderSelf]   = useState(false);
  const [activeSection, setActiveSection] = useState('personal');
  // New staff only: compliance captured now, saved once the record exists.
  const [pendingQuals, setPendingQuals] = useState([]);
  const [newQual, setNewQual]         = useState({ name: '', expiryDate: '' });
  const [clearance, setClearance]     = useState({ number: '', expiry: '', state: '' });
  const [created, setCreated]         = useState(null);   // { id, name, email, tempPassword, welcomeEmailSent, problems, noPay }
  const [inviteSending, setInviteSending] = useState(false);
  const jobTitles = useJobTitles();

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => { const n = { ...e }; delete n[k]; return n; });
  };

  useEffect(() => {
    api.get('/pay-groups').then((r) => setPayGroupOptions(r.data || [])).catch(() => {});
    api.get('/staff').then((r) => setStaffList(Array.isArray(r.data) ? r.data : (r.data?.staff || []))).catch(() => {});
    api.get('/smart-match').then((r) => setMasterQuals(r.data?.qualifications || [])).catch(() => {});
  }, []);

  const loadRecord = useCallback(() => {
    if (!isEdit) return;
    setLoading(true);
    setLoadError(null);
    api.get(`/staff/${id}`)
      .then((r) => {
        const s = r.data;
        const next = {
          firstName: s.first_name || '', lastName: s.last_name || '', email: s.email || '',
          phone: s.phone || '', address: s.address || '', suburb: s.suburb || '', state: s.state || '', postcode: s.postcode || '',
          nationality: s.nationality || '', employeeNumber: s.employee_number || '',
          dateOfBirth: s.date_of_birth ? String(s.date_of_birth).slice(0, 10) : '', gender: s.gender || '',
          role: s.role || 'carer', jobTitle: s.job_title || '', employmentType: s.employment_type || '',
          payGroup: s.pay_group || '', overrideHourlyRate: s.override_hourly_rate || '',
          contractorWeekdayRate: s.contractor_weekday_rate ?? '', contractorSaturdayRate: s.contractor_saturday_rate ?? '',
          contractorSundayRate: s.contractor_sunday_rate ?? '', contractorPhRate: s.contractor_ph_rate ?? '',
          contractorSuperApplies: !!s.contractor_super_applies,
          gstRegistered: s.gst_registered === true,
          dailyHoursCap: s.daily_hours_cap || '', weeklyHoursCap: s.weekly_hours_cap || '',
          standardDailyHours: s.standard_daily_hours || '',
          externalSystemId: s.external_system_id || '',
          startDate: s.start_date ? String(s.start_date).slice(0, 10) : '', endDate: s.end_date ? String(s.end_date).slice(0, 10) : '',
          emergencyContactName: s.emergency_contact_name || '',
          emergencyContactRelationship: s.emergency_contact_relationship || '',
          emergencyContactPhone: s.emergency_contact_phone || '',
          emergencyContactEmail: s.emergency_contact_email || '',
          tfn: s.tfn || '', abn: s.abn || '',
          bankBsb: s.bank_bsb || '', bankAccountNumber: s.bank_account_number || '', bankAccountName: s.bank_account_name || '',
          superFundName: s.super_fund_name || '', superMemberNumber: s.super_member_number || '',
          carRegistration: s.car_registration || '', carMake: s.car_make || '',
          carModel: s.car_model || '', carColor: s.car_color || '',
          carExpiry: s.car_expiry ? String(s.car_expiry).slice(0, 10) : '',
          managerId: s.manager_id || '', onHold: !!s.on_hold,
          // Not edited on this form, but carried through: the save replaces the whole record, and
          // leaving these out used to wipe a worker's languages and reset their approval emails.
          languagesSpoken: s.languages_spoken || [],
          notifyAttendanceApproval: s.notify_attendance_approval ?? true,
        };
        initialRef.current = JSON.stringify(next);
        setForm(next);
        setGenderSelf(!!(s.gender && !genderOption(s.gender)));
        setRecord(s);
        setPhotoUrl(s.profile_photo_url || null);
      })
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load this staff member. Check your connection, then try again."))
      .finally(() => setLoading(false));
  }, [id, isEdit]);
  useEffect(() => { loadRecord(); }, [loadRecord]);

  const dirty = JSON.stringify(form) !== initialRef.current || pendingQuals.length > 0 || !!clearance.number;
  useBeforeUnload(dirty && !saving && !created);

  // Jump to the section named in the address (the profile's Edit links use #pay, #screening…)
  useEffect(() => {
    if (loading || !hash) return undefined;
    const t = setTimeout(() => {
      const el = document.getElementById(hash.slice(1));
      if (el) el.scrollIntoView({ block: 'start' });
    }, 50);
    return () => clearTimeout(t);
  }, [loading, hash]);

  // Highlight the section in view
  useEffect(() => {
    if (loading) return undefined;
    const els = SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean);
    if (!els.length || typeof IntersectionObserver === 'undefined') return undefined;
    const obs = new IntersectionObserver((entries) => {
      const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (vis[0]) setActiveSection(vis[0].target.id);
    }, { rootMargin: '-80px 0px -60% 0px' });
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [loading]);

  /* ── AI auto-fill ── */
  const handleAiExtract = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setExtracting(true);
    setFilledFields([]);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post('/staff/extract-from-cv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const ex = data.extracted || {};
      const map = {
        firstName: ex.firstName, lastName: ex.lastName, email: ex.email, phone: ex.phone,
        address: ex.address, dateOfBirth: ex.dateOfBirth, gender: ex.gender,
        role: ex.role, jobTitle: ex.jobTitle,
        emergencyContactName: ex.emergencyContactName,
        emergencyContactRelationship: ex.emergencyContactRelationship,
        emergencyContactPhone: ex.emergencyContactPhone,
        emergencyContactEmail: ex.emergencyContactEmail,
      };
      const filled = [];
      setForm((f) => {
        const next = { ...f };
        for (const [k, v] of Object.entries(map)) {
          if (v && (!f[k] || f[k] === '' || f[k] === 'carer')) { next[k] = v; filled.push(k); }
        }
        return next;
      });
      if (map.gender && !genderOption(map.gender)) setGenderSelf(true);
      setFilledFields(filled);
      const found = (ex.qualifications || []).filter((q) => q && q.name);
      if (!isEdit && found.length) {
        setPendingQuals((list) => {
          const have = new Set(list.map((q) => q.name.toLowerCase()));
          return [...list, ...found.filter((q) => !have.has(String(q.name).toLowerCase())).map((q) => ({ name: q.name, expiryDate: q.expiryDate || '', fromCv: true }))];
        });
      }
      toast.success(`Filled ${filled.length} field${filled.length === 1 ? '' : 's'} from the CV${found.length ? ` and found ${found.length} qualification${found.length === 1 ? '' : 's'}` : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.message || "The CV couldn't be read. Fill the form in by hand, or try another file.");
    } finally {
      setExtracting(false);
    }
  };
  const aiFilled = (k) => filledFields.includes(k);

  /* ── validation ── */
  // What the form held when it was loaded (or last saved), to tell which fields were changed here.
  const initial = JSON.parse(initialRef.current);
  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Enter a first name.';
    if (!form.lastName.trim()) e.lastName = 'Enter a last name.';
    if (!form.email.trim()) e.email = 'Enter an email address. It is their sign-in.';
    else if (!EMAIL_RE.test(form.email.trim())) e.email = 'Enter a valid email address, like name@example.com.';
    if (form.emergencyContactEmail && !EMAIL_RE.test(form.emergencyContactEmail.trim())) e.emergencyContactEmail = 'Enter a valid email address.';
    // Only check what was changed here, so an old value in an untouched field never blocks a save.
    if (form.tfn !== initial.tfn && form.tfn && ![8, 9].includes(digitsOnly(form.tfn).length)) e.tfn = 'A TFN has 8 or 9 digits.';
    if (form.bankBsb !== initial.bankBsb && form.bankBsb && digitsOnly(form.bankBsb).length !== 6) e.bankBsb = 'A BSB has 6 digits.';
    if (form.bankAccountNumber !== initial.bankAccountNumber && form.bankAccountNumber && !/^\d{5,10}$/.test(digitsOnly(form.bankAccountNumber))) e.bankAccountNumber = 'Use 5 to 10 digits.';
    if (form.endDate && form.startDate && form.endDate < form.startDate) e.endDate = 'The end date is before the start date.';
    return e;
  };

  const focusFirstError = (e) => {
    const first = ['firstName', 'lastName', 'email', 'tfn', 'bankBsb', 'bankAccountNumber', 'endDate', 'emergencyContactEmail'].find((k) => e[k]);
    if (!first) return;
    setTimeout(() => {
      const el = document.querySelector(`[data-field="${first}"]`);
      if (el) { el.scrollIntoView({ block: 'center' }); el.focus({ preventScroll: true }); }
    }, 0);
  };

  /* ── save ── */
  const doSave = async () => {
    setSubmitted(true);
    setSaveError('');
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) { focusFirstError(e); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/staff/${id}`, form);
        forgetStaffRecord(id);
        initialRef.current = JSON.stringify(form);
        toast.success('Changes saved');
        navigate(`/staff/${id}`);
        return;
      }
      const { data } = await api.post('/staff', form);
      const newId = data.id;
      const problems = [];
      // Save everything the create step doesn't take (job title, pay, tax, bank, vehicle…)
      if (AFTER_CREATE.some((k) => form[k] !== EMPTY[k] && form[k] !== '' && form[k] != null)) {
        try { await api.put(`/staff/${newId}`, form); }
        catch (err) { problems.push(`Job, pay, tax, bank and vehicle details: ${err.response?.data?.message || 'not saved'}`); }
      }
      for (const q of pendingQuals) {
        try {
          const def = masterQuals.find((m) => m.name.toLowerCase() === q.name.toLowerCase());
          await api.post(`/staff/${newId}/qualifications`, {
            qualificationName: def ? def.name : q.name,
            qual_definition_id: def ? def.id : null,
            expiryDate: q.expiryDate || null,
            verificationStatus: 'not_verified',
            appVisible: true,
          });
        } catch { problems.push(`Qualification "${q.name}"`); }
      }
      let screening = null;
      if (clearance.number.trim()) {
        try {
          const { data: v } = await api.post(`/staff/${newId}/verify-clearance`, {
            clearanceNumber: clearance.number, clearanceExpiry: clearance.expiry || null, clearanceState: clearance.state || null,
          });
          screening = v;
        } catch (err) { problems.push(`NDIS screening: ${err.response?.data?.message || 'not saved'}`); }
      }
      initialRef.current = JSON.stringify(form);
      setPendingQuals([]);
      setClearance({ number: '', expiry: '', state: '' });
      const paidRole = ['carer', 'support_worker', 'staff'].includes(form.role);
      const noPay = paidRole && (form.employmentType === 'contractor' ? !(parseFloat(form.contractorWeekdayRate) > 0) : !(form.payGroup || form.overrideHourlyRate));
      setCreated({
        id: newId, name: `${form.firstName} ${form.lastName}`.trim(), email: data.email || form.email,
        tempPassword: data.tempPassword || null, welcomeEmailSent: !!data.welcomeEmailSent, problems, noPay,
        screening: screening && screening.status !== 'clear' ? screening.message : null,
      });
    } catch (err) {
      const msg = err.response?.data?.message || "The staff member wasn't saved. Check your connection and try again.";
      if (/email/i.test(msg)) { setErrors((x) => ({ ...x, email: msg })); focusFirstError({ email: msg }); }
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };
  const handleSubmit = (e) => { e.preventDefault(); if (!saving) doSave(); };

  const cancelTo = isEdit ? `/staff/${id}` : '/staff';
  const cancel = async () => {
    if (dirty && !await confirm('Your changes on this form will be lost.', { title: 'Discard unsaved changes?', confirmLabel: 'Discard', danger: true })) return;
    initialRef.current = JSON.stringify(form);
    navigate(cancelTo);
  };

  const sendInvite = async () => {
    if (!created) return;
    setInviteSending(true);
    try {
      await api.post('/auth/invite/send', { userId: created.id });
      toast.success(`App invite sent to ${created.email}`);
    } catch (e) { toast.error(e.response?.data?.message || "The invite didn't send. Try again from their profile."); }
    finally { setInviteSending(false); }
  };
  const goToProfile = useStableCallback(() => { if (created) navigate(`/staff/${created.id}`); });

  /* ── render ── */
  if (loading) {
    return (
      <div className={PAGE}>
        <div className="skeleton h-4 w-24" />
        <div className="rounded-xl border border-slate-200 bg-white"><TableSkeleton rows={8} cols={2} /></div>
      </div>
    );
  }
  if (loadError) {
    return (
      <div className={PAGE}>
        <Link to="/staff" className="inline-flex items-center gap-1 text-[13px] font-medium text-slate-500 hover:text-slate-800"><ChevronLeft size={14} aria-hidden="true" />Staff</Link>
        <div className="rounded-xl border border-slate-200 bg-white"><ErrorState message={loadError} onRetry={loadRecord} /></div>
      </div>
    );
  }

  const fullName = [form.firstName, form.lastName].filter(Boolean).join(' ');
  const selectedPg = payGroupOptions.find((pg) => pg.name === form.payGroup);
  const isContractor = form.employmentType === 'contractor';
  const paidRole = ['carer', 'support_worker', 'staff'].includes(form.role);
  const noPay = paidRole && (isContractor ? !(parseFloat(form.contractorWeekdayRate) > 0) : !(form.payGroup || form.overrideHourlyRate));
  // Only raise the alarm once it matters: after a save attempt, or on a worker who has worked shifts.
  const escalatePay = noPay && (submitted || (isEdit && Number(record?.total_shifts_completed) > 0));
  const jobTitleOptions = jobTitles.filter((j) => j.is_active).map((j) => j.name);
  if (form.jobTitle && !jobTitleOptions.includes(form.jobTitle)) jobTitleOptions.unshift(form.jobTitle);
  const genderChoice = genderSelf ? 'self_describe' : ((genderOption(form.gender) || {}).value || '');

  // Field builders are plain functions (not components) so inputs keep focus while typing.
  const text = (k, label, opts = {}) => {
    const { required, hint, type = 'text', placeholder, autoComplete, inputMode, maxLength, span, readOnly } = opts;
    return (
      <Field label={<>{label}{aiFilled(k) && <AiTag />}</>} required={required} hint={hint} error={errors[k]} className={span}>
        {(fid) => (
          <input id={fid} data-field={k} type={type} className="input-field" value={form[k] ?? ''} placeholder={placeholder}
            autoComplete={autoComplete} inputMode={inputMode} maxLength={maxLength} aria-invalid={!!errors[k] || undefined}
            readOnly={readOnly} onChange={(e) => set(k, e.target.value)} />
        )}
      </Field>
    );
  };
  const money$ = (k, label, opts = {}) => (
    <Field label={label} hint={opts.hint} error={errors[k]}>
      {(fid) => (
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">$</span>
          <input id={fid} data-field={k} type="number" step="0.01" min="0" inputMode="decimal" className="input-field pl-7 tabular-nums" placeholder="0.00"
            value={form[k] ?? ''} onChange={(e) => set(k, e.target.value)} />
        </div>
      )}
    </Field>
  );

  // Pay preview — the same arithmetic as before, written as money.
  const previewBase = parseFloat(form.overrideHourlyRate || (selectedPg && selectedPg.base_hourly_rate) || 0);
  const satX = parseFloat(selectedPg && selectedPg.saturday_mult) || 1.5;
  const sunX = parseFloat(selectedPg && selectedPg.sunday_mult) || 2.0;
  const phX = parseFloat(selectedPg && selectedPg.ph_mult) || 2.5;
  const wk = parseFloat(form.contractorWeekdayRate) || 0;
  const eff = (v) => { const n = parseFloat(v); return n > 0 ? n : wk; };

  return (
    <div className={PAGE}>
      {/* Header: back, who this is, and the two things you can do from the top */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          {isEdit ? (
            <ProfilePhotoUpload entity="staff" id={id} photo={photoUrl} firstName={form.firstName} lastName={form.lastName}
              gradient={avatarColor(id)} onChange={setPhotoUrl} />
          ) : null}
          <div className="min-w-0">
            <button type="button" onClick={cancel} className="inline-flex items-center gap-1 text-[13px] font-medium text-slate-500 hover:text-slate-800">
              <ChevronLeft size={14} aria-hidden="true" />{isEdit ? (fullName || 'Staff member') : 'Staff'}
            </button>
            <h1 className="mt-1 truncate text-[22px] font-semibold leading-tight tracking-[-0.02em] text-slate-900">
              {isEdit ? `Edit ${fullName || 'staff member'}` : (fullName || 'New staff member')}
            </h1>
            <p className="mt-0.5 text-sm text-slate-500">
              {[roleLabel(form.role), form.employmentType ? employmentLabel(form.employmentType) : null, form.jobTitle].filter((v) => v && v !== '—').join(' · ') || 'Fill in their details below.'}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" className="hidden" accept=".pdf,.docx,.doc,.txt,image/*" onChange={handleAiExtract} disabled={extracting} />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={extracting} className="btn-secondary btn-sm">
            {extracting ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Sparkles size={13} className="text-slate-500" aria-hidden="true" />}
            {extracting ? 'Reading CV…' : 'Fill from CV'}
          </button>
          <button type="submit" form="staff-form" disabled={saving} className="btn-primary btn-sm">
            {saving ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Save size={13} aria-hidden="true" />}
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create staff member'}
          </button>
        </div>
      </header>

      {/* AI result: a plain notice; the filled fields carry a small AI tag */}
      {filledFields.length > 0 && (
        <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
          <Sparkles size={16} className="mt-0.5 shrink-0 text-slate-500" aria-hidden="true" />
          <div className="min-w-0 flex-1 text-[13px] text-slate-700">
            <p className="font-medium text-slate-900">Filled {filledFields.length} field{filledFields.length === 1 ? '' : 's'} from the CV</p>
            <p className="mt-0.5 text-slate-600">Check each one marked AI before saving.{!isEdit && pendingQuals.some((q) => q.fromCv) ? ' Qualifications it found are listed under Compliance.' : ''}</p>
          </div>
          <IconButton label="Dismiss" icon={X} onClick={() => setFilledFields([])} />
        </div>
      )}

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        {/* Section nav */}
        <nav aria-label="Form sections" className="lg:sticky lg:top-20 lg:w-44 lg:shrink-0">
          <ul className="flex gap-1 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {SECTIONS.map((s) => (
              <li key={s.id} className="shrink-0">
                <a href={`#${s.id}`} aria-current={activeSection === s.id ? 'true' : undefined}
                  onClick={(e) => { e.preventDefault(); const el = document.getElementById(s.id); if (el) { el.scrollIntoView({ block: 'start' }); setActiveSection(s.id); } }}
                  className={clsx('block whitespace-nowrap rounded-lg px-3 py-1.5 text-[13px] font-medium transition-colors duration-150',
                    activeSection === s.id ? 'bg-white text-slate-900 shadow-[0_1px_2px_rgba(16,24,40,.08)] ring-1 ring-slate-200' : 'text-slate-500 hover:bg-white/70 hover:text-slate-800')}>
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <form id="staff-form" onSubmit={handleSubmit} noValidate className="min-w-0 flex-1 space-y-5">
          <FormSection id="personal" title="Personal details">
            <FormRow>
              {text('firstName', 'First name', { required: true, autoComplete: 'given-name' })}
              {text('lastName', 'Last name', { required: true, autoComplete: 'family-name' })}
            </FormRow>
            <FormRow cols={3}>
              {text('dateOfBirth', 'Date of birth', { type: 'date', autoComplete: 'bday' })}
              <Field label={<>Gender{aiFilled('gender') && <AiTag />}</>}>
                {(fid) => (
                  <div className="space-y-2">
                    <select id={fid} className="input-field" value={genderChoice}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === 'self_describe') { setGenderSelf(true); set('gender', genderOption(form.gender) ? '' : form.gender); }
                        else { setGenderSelf(false); set('gender', v); }
                      }}>
                      <option value="">Not stated</option>
                      {GENDER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      <option value="self_describe">Self-describe</option>
                    </select>
                    {genderSelf && (
                      <input aria-label="Gender, in their words" className="input-field" value={form.gender} placeholder="In their words"
                        onChange={(e) => set('gender', e.target.value)} />
                    )}
                  </div>
                )}
              </Field>
              {text('nationality', 'Nationality', { autoComplete: 'country-name' })}
            </FormRow>
          </FormSection>

          <FormSection id="contact" title="Contact">
            <FormRow>
              {text('email', 'Email', { required: true, type: 'email', autoComplete: 'email', hint: 'They sign in with this.' })}
              {text('phone', 'Mobile', { type: 'tel', autoComplete: 'tel', inputMode: 'tel' })}
            </FormRow>
            <Field label={<>Home address{aiFilled('address') && <AiTag />}</>} hint="Used to work out travel distance to clients.">
              {(fid) => (
                <AddressAutocomplete id={fid} className="input-field" value={form.address}
                  onChange={(v) => set('address', v)}
                  onSelect={(a) => setForm((f) => ({
                    ...f,
                    address: [a.street, a.suburb, a.state, a.postcode].filter(Boolean).join(', '),
                    suburb: a.suburb || f.suburb, state: a.state || f.state, postcode: a.postcode || f.postcode,
                  }))} />
              )}
            </Field>
            <FormRow cols={3}>
              {text('suburb', 'Suburb', { autoComplete: 'address-level2' })}
              <Field label="State">
                {(fid) => (
                  <select id={fid} className="input-field" value={form.state} onChange={(e) => set('state', e.target.value)}>
                    <option value="">—</option>
                    {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
              </Field>
              {text('postcode', 'Postcode', { autoComplete: 'postal-code', inputMode: 'numeric', maxLength: 4 })}
            </FormRow>
          </FormSection>

          <FormSection id="role" title="Role and employment">
            <FormRow>
              <Field label="Role" required hint="Decides what they can see and do in Rostery.">
                {(fid) => (
                  <select id={fid} className="input-field" value={form.role} onChange={(e) => set('role', e.target.value)}>
                    <option value="carer">Carer</option>
                    <option value="office_user">Office user</option>
                    <option value="admin">Admin</option>
                  </select>
                )}
              </Field>
              <Field label={<>Job title{aiFilled('jobTitle') && <AiTag />}</>}>
                {(fid) => (
                  <select id={fid} className="input-field" value={form.jobTitle} onChange={(e) => set('jobTitle', e.target.value)}>
                    <option value="">None</option>
                    {jobTitleOptions.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                )}
              </Field>
            </FormRow>
            <FormRow cols={3}>
              <Field label="Employment type">
                {(fid) => (
                  <select id={fid} className="input-field" value={form.employmentType} onChange={(e) => set('employmentType', e.target.value)}>
                    <option value="">Not set</option>
                    {EMPLOYMENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                )}
              </Field>
              {text('employeeNumber', 'Employee number')}
              {text('externalSystemId', 'Payroll ID', { hint: 'Their ID in your payroll system, such as MYOB.' })}
            </FormRow>
            <FormRow>
              {text('startDate', 'Start date', { type: 'date' })}
              {text('endDate', 'End date', { type: 'date', hint: 'Leave blank while they still work here.' })}
            </FormRow>
            <FormRow>
              <Field label="Manager" hint="Emailed about this worker's leave requests and incidents.">
                {(fid) => (
                  <select id={fid} className="input-field" value={form.managerId} onChange={(e) => set('managerId', e.target.value)}>
                    <option value="">None</option>
                    {staffList.filter((s) => s.id !== id).map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                  </select>
                )}
              </Field>
              <div className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2.5 sm:mt-6">
                <span className="text-sm text-slate-700">
                  On hold
                  <span className="block text-[12px] text-slate-500">Leave them off the roster while onboarding or while a check is outstanding.</span>
                </span>
                <Switch checked={form.onHold} onChange={(v) => set('onHold', v)} label="On hold" />
              </div>
            </FormRow>
          </FormSection>

          <FormSection id="pay" title={isContractor ? 'Contractor pay' : 'Pay'}
            description={isContractor ? 'Flat agreed rates. No award loadings apply, and the client is still charged from their price book.' : 'How approved timesheets are paid.'}>
            {isContractor ? (
              <>
                <FormRow cols={4}>
                  {money$('contractorWeekdayRate', 'Weekday (Mon–Fri)', { hint: 'The base rate' })}
                  {money$('contractorSaturdayRate', 'Saturday', { hint: 'Blank uses the weekday rate' })}
                  {money$('contractorSundayRate', 'Sunday', { hint: 'Blank uses the weekday rate' })}
                  {money$('contractorPhRate', 'Public holiday', { hint: 'Blank uses the weekday rate' })}
                </FormRow>
                {wk > 0 && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3">
                    <p className="text-[12.5px] font-semibold text-slate-700">What this contractor is paid per hour</p>
                    <dl className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                      {[['Weekday', wk, false], ['Saturday', eff(form.contractorSaturdayRate), !(parseFloat(form.contractorSaturdayRate) > 0)],
                        ['Sunday', eff(form.contractorSundayRate), !(parseFloat(form.contractorSundayRate) > 0)],
                        ['Public holiday', eff(form.contractorPhRate), !(parseFloat(form.contractorPhRate) > 0)]].map(([lbl, r, fb]) => (
                        <div key={lbl} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                          <dt className="text-[11.5px] text-slate-500">{lbl}{fb ? ' *' : ''}</dt>
                          <dd className="mt-0.5 text-sm font-semibold tabular-nums text-slate-900">{money(r)}</dd>
                        </div>
                      ))}
                    </dl>
                    <p className="mt-2 text-[11.5px] text-slate-500">* Uses the weekday rate. Applied by the shift's date, with no award loadings.</p>
                  </div>
                )}
                <div className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2.5">
                  <span className="text-sm text-slate-700">
                    Pay superannuation
                    <span className="block text-[12px] leading-relaxed text-slate-500">Turn on if they're paid wholly or mainly for their labour: the ATO treats them as an employee for super only (SGA s12(3)), even with an ABN. Leave off for genuine business-to-business contractors.</span>
                  </span>
                  <Switch checked={!!form.contractorSuperApplies} onChange={(v) => set('contractorSuperApplies', v)} label="Pay superannuation" />
                </div>
                <div className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2.5">
                  <span className="text-sm text-slate-700">
                    Registered for GST
                    <span className="block text-[12px] leading-relaxed text-slate-500">Their invoices from Pay contractors then add 10% GST to the services (not to km or expenses). Check their ABN on the ABN Lookup site if you're not sure.</span>
                  </span>
                  <Switch checked={!!form.gstRegistered} onChange={(v) => set('gstRegistered', v)} label="Registered for GST" />
                </div>
                <p className="text-[12px] text-slate-500">Add their ABN and bank details under Tax &amp; bank. With no ABN quoted you may need to withhold 47% PAYG.</p>
              </>
            ) : (
              <>
                <FormRow>
                  <Field label="Pay group (SCHADS award)" hint={noPay && !escalatePay ? 'Needed to calculate their pay.' : undefined}>
                    {(fid) => (
                      <select id={fid} className="input-field" value={form.payGroup} onChange={(e) => {
                        const pgName = e.target.value;
                        setForm((f) => {
                          const pg = payGroupOptions.find((p) => p.name === pgName);
                          // Choosing an award level fills in its base rate (editable for above-award workers).
                          const rate = pg && pg.base_hourly_rate != null && pg.base_hourly_rate !== '' ? String(pg.base_hourly_rate) : f.overrideHourlyRate;
                          return { ...f, payGroup: pgName, overrideHourlyRate: rate };
                        });
                      }}>
                        <option value="">None</option>
                        {payGroupOptions.map((pg) => (
                          <option key={pg.id} value={pg.name}>{pg.name}{pg.base_hourly_rate != null && pg.base_hourly_rate !== '' ? ` — ${money(pg.base_hourly_rate)}/hr` : ''}</option>
                        ))}
                      </select>
                    )}
                  </Field>
                  {money$('overrideHourlyRate', 'Hourly rate', { hint: 'Filled in from the award. Change it for an above-award rate.' })}
                </FormRow>
                {previewBase > 0 && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3">
                    <p className="text-[12.5px] font-semibold text-slate-700">Effective hourly rates{form.overrideHourlyRate ? ' (custom rate)' : selectedPg ? `, ${selectedPg.name}` : ''}</p>
                    <dl className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                      {[['Weekday', previewBase], ...[['Saturday', satX], ['Sunday', sunX], ['Public holiday', phX]].map(([d, x]) => {
                        const o = { override: !!form.overrideHourlyRate };
                        return [d, penaltyRate(previewBase, x, selectedPg, o)];
                      })].map(([lbl, r]) => (
                        <div key={lbl} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                          <dt className="text-[11.5px] text-slate-500">{lbl}</dt>
                          <dd className="mt-0.5 text-sm font-semibold tabular-nums text-slate-900">{money(r)}</dd>
                        </div>
                      ))}
                    </dl>
                    <p className="mt-2 text-[11.5px] text-slate-500">Base {money(previewBase)}/hr{form.overrideHourlyRate ? ' (custom)' : ''}. Penalties come from {selectedPg ? selectedPg.name : 'the award'}.</p>
                  </div>
                )}
              </>
            )}
            {escalatePay && (
              <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" role="status">
                <AlertTriangle size={15} className="mt-0.5 shrink-0 text-amber-700" aria-hidden="true" />
                <div>
                  <p className="text-[13px] font-semibold text-amber-900">No {isContractor ? 'weekday rate' : 'pay group'} set</p>
                  <p className="mt-0.5 text-[12.5px] leading-relaxed text-amber-800">
                    Their approved timesheets will calculate to $0 pay until you {isContractor ? 'enter a weekday rate' : 'choose a pay group or enter an hourly rate'}. Invoicing isn't affected: it uses the client's price book.
                  </p>
                </div>
              </div>
            )}
            <FormRow cols={3}>
              {text('standardDailyHours', 'Standard daily hours', { type: 'number', inputMode: 'decimal' })}
              {text('dailyHoursCap', 'Daily hours cap', { type: 'number', inputMode: 'decimal' })}
              {text('weeklyHoursCap', 'Weekly hours cap', { type: 'number', inputMode: 'decimal' })}
            </FormRow>
          </FormSection>

          <FormSection id="tax" title="Tax, bank and super" description="Stored encrypted. Shown masked on their profile.">
            <FormRow>
              <Field label="Tax file number (TFN)" error={errors.tfn} hint={!tfnFocus && form.tfn ? 'Hidden. Click the field to see or change it.' : '8 or 9 digits.'}>
                {(fid) => (
                  <input id={fid} data-field="tfn" className="input-field tabular-nums" inputMode="numeric" autoComplete="off" maxLength={11}
                    value={tfnFocus ? form.tfn : maskTail(form.tfn)} placeholder="123 456 789" aria-invalid={!!errors.tfn || undefined}
                    onFocus={() => setTfnFocus(true)} onBlur={() => setTfnFocus(false)} readOnly={!tfnFocus}
                    onChange={(e) => set('tfn', e.target.value.replace(/[^\d ]/g, ''))} />
                )}
              </Field>
              {text('abn', 'ABN', { inputMode: 'numeric', maxLength: 14, placeholder: '12 345 678 901', hint: 'For contractors.' })}
            </FormRow>
            <FormRow cols={3}>
              <Field label="BSB" error={errors.bankBsb} hint={bankLocked ? 'Changing bank details needs the Pay Rates: Edit permission.' : undefined}>
                {(fid) => (
                  <input id={fid} data-field="bankBsb" className="input-field tabular-nums" inputMode="numeric" autoComplete="off" maxLength={7}
                    value={form.bankBsb} placeholder="000-000" aria-invalid={!!errors.bankBsb || undefined} readOnly={bankLocked}
                    onChange={(e) => set('bankBsb', fmtBsb(e.target.value))} />
                )}
              </Field>
              <Field label="Account number" error={errors.bankAccountNumber}>
                {(fid) => (
                  <input id={fid} data-field="bankAccountNumber" className="input-field tabular-nums" inputMode="numeric" autoComplete="off" maxLength={15}
                    value={form.bankAccountNumber} aria-invalid={!!errors.bankAccountNumber || undefined} readOnly={bankLocked}
                    onChange={(e) => set('bankAccountNumber', e.target.value.replace(/[^\d ]/g, ''))} />
                )}
              </Field>
              {text('bankAccountName', 'Account name', { readOnly: bankLocked })}
            </FormRow>
            <FormRow>
              {text('superFundName', 'Super fund', { placeholder: 'e.g. AustralianSuper' })}
              {text('superMemberNumber', 'Member number')}
            </FormRow>
          </FormSection>

          <FormSection id="emergency" title="Emergency contact">
            <FormRow>
              {text('emergencyContactName', 'Full name')}
              {text('emergencyContactRelationship', 'Relationship', { placeholder: 'e.g. Partner, parent' })}
            </FormRow>
            <FormRow>
              {text('emergencyContactPhone', 'Phone', { type: 'tel', inputMode: 'tel' })}
              {text('emergencyContactEmail', 'Email', { type: 'email' })}
            </FormRow>
          </FormSection>

          <FormSection id="vehicle" title="Vehicle" description="For workers who drive their own car to shifts.">
            <FormRow>
              {text('carRegistration', 'Registration', { placeholder: 'ABC123' })}
              {text('carExpiry', 'Registration expiry', { type: 'date' })}
            </FormRow>
            <FormRow cols={3}>
              {text('carMake', 'Make', { placeholder: 'Toyota' })}
              {text('carModel', 'Model', { placeholder: 'Corolla' })}
              {text('carColor', 'Colour', { placeholder: 'White' })}
            </FormRow>
          </FormSection>

          <FormSection id="compliance" title="Compliance" description={isEdit ? 'Changes here save straight away.' : 'Saved as soon as the staff member is created.'}>
            {isEdit ? (
              <>
                <QualificationsEditor staffId={id} initialQuals={record?.qualifications || []} masterQuals={masterQuals} />
                <ScreeningEditor staffId={id} record={record} />
              </>
            ) : (
              <>
                <div>
                  <h3 className="mb-2 text-[13px] font-semibold text-slate-700">Qualifications</h3>
                  {pendingQuals.length > 0 && (
                    <ul className="mb-3 divide-y divide-slate-100 rounded-lg border border-slate-200">
                      {pendingQuals.map((q, i) => (
                        <li key={`${q.name}-${i}`} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2">
                          <span className="min-w-0 flex-1 text-sm font-medium text-slate-800">{q.name}{q.fromCv && <AiTag />}</span>
                          <input type="date" aria-label={`Expiry date for ${q.name}`} value={q.expiryDate}
                            onChange={(e) => setPendingQuals((list) => list.map((x, j) => (j === i ? { ...x, expiryDate: e.target.value } : x)))}
                            className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[12.5px] text-slate-700" />
                          <IconButton label={`Remove ${q.name}`} icon={Trash2} size={14} danger onClick={() => setPendingQuals((list) => list.filter((_, j) => j !== i))} />
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <Field label="Qualification" className="flex-1">
                      {(fid) => (
                        <>
                          <input id={fid} list="staff-master-quals" className="input-field" value={newQual.name} placeholder="Start typing, e.g. First Aid"
                            onChange={(e) => setNewQual((q) => ({ ...q, name: e.target.value }))} />
                          <datalist id="staff-master-quals">{masterQuals.map((m) => <option key={m.id} value={m.name} />)}</datalist>
                        </>
                      )}
                    </Field>
                    <Field label="Expiry date">
                      {(fid) => <input id={fid} type="date" className="input-field" value={newQual.expiryDate} onChange={(e) => setNewQual((q) => ({ ...q, expiryDate: e.target.value }))} />}
                    </Field>
                    <button type="button" className="btn-secondary btn-sm sm:mb-1" disabled={!newQual.name.trim()}
                      onClick={() => { setPendingQuals((list) => [...list, { name: newQual.name.trim(), expiryDate: newQual.expiryDate }]); setNewQual({ name: '', expiryDate: '' }); }}>
                      <Plus size={13} aria-hidden="true" />Add
                    </button>
                  </div>
                </div>
                <div id="screening" className="scroll-mt-24">
                  <h3 className="mb-2 text-[13px] font-semibold text-slate-700">NDIS worker screening</h3>
                  <FormRow cols={3}>
                    <Field label="Clearance number">
                      {(fid) => <input id={fid} className="input-field" autoComplete="off" value={clearance.number} onChange={(e) => setClearance((c) => ({ ...c, number: e.target.value }))} />}
                    </Field>
                    <Field label="Expiry date">
                      {(fid) => <input id={fid} type="date" className="input-field" value={clearance.expiry} onChange={(e) => setClearance((c) => ({ ...c, expiry: e.target.value }))} />}
                    </Field>
                    <Field label="State issued">
                      {(fid) => (
                        <select id={fid} className="input-field" value={clearance.state} onChange={(e) => setClearance((c) => ({ ...c, state: e.target.value }))}>
                          <option value="">—</option>
                          {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      )}
                    </Field>
                  </FormRow>
                  <p className="mt-2 text-[12px] text-slate-500">We check the number's format and expiry when you save. Confirm it on the NDIS Commission portal too.</p>
                </div>
              </>
            )}
          </FormSection>

          {/* Save bar — stays in view at the bottom of the screen */}
          <div className="sticky bottom-[calc(57px_+_env(safe-area-inset-bottom))] z-20 md:bottom-3">
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-card-deep">
              {saveError ? (
                <span className="flex min-w-0 items-center gap-1.5 text-[13px] font-medium text-rose-700" role="alert">
                  <AlertTriangle size={14} className="shrink-0" aria-hidden="true" /><span className="truncate">{saveError}</span>
                </span>
              ) : dirty ? (
                <span className="flex items-center gap-1.5 text-[13px] font-medium text-amber-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />Unsaved changes
                </span>
              ) : (
                <span className="text-[13px] text-slate-500">{isEdit ? 'No changes yet' : 'Fill in at least a name and email'}</span>
              )}
              <div className="ml-auto flex items-center gap-2">
                <button type="button" onClick={cancel} className="btn-secondary btn-sm" disabled={saving}>Cancel</button>
                <button type="submit" disabled={saving} className="btn-primary btn-sm">
                  {saving ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Save size={13} aria-hidden="true" />}
                  {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create staff member'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Created: hand over the sign-in details before leaving the page */}
      <Dialog open={!!created} onClose={goToProfile} dismissable={false} hideClose size="md"
        title={created ? `${created.name || 'Staff member'} is set up` : ''}
        footer={created && (
          <>
            <button type="button" onClick={sendInvite} disabled={inviteSending || !created.email} className="btn-secondary">
              <Mail size={14} aria-hidden="true" />{inviteSending ? 'Sending…' : 'Send app invite'}
            </button>
            <button type="button" onClick={goToProfile} className="btn-primary" data-autofocus>Go to profile</button>
          </>
        )}>
        {created && (
          <div className="space-y-4 text-sm text-slate-700">
            {created.tempPassword ? (
              <div>
                <p className="text-[13px] font-medium text-slate-600">Temporary password</p>
                <div className="mt-1 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <code className="flex-1 select-all font-mono text-[15px] tracking-wide text-slate-900">{created.tempPassword}</code>
                  <CopyButton value={created.tempPassword} label="Copy" title="Copy password" />
                </div>
                <p className="mt-1.5 text-[12.5px] text-slate-500">
                  {created.welcomeEmailSent
                    ? `We also emailed it to ${created.email}. `
                    : `The welcome email didn't go out, so hand this over yourself or send an app invite. `}
                  Ask them to change it after they first sign in. It won't be shown again.
                </p>
              </div>
            ) : (
              <p>{created.welcomeEmailSent ? `We emailed their sign-in details to ${created.email}.` : 'Send them an app invite so they can sign in.'}</p>
            )}
            {created.noPay && (
              <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-900">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
                No pay group or rate is set, so their approved timesheets will calculate to $0 pay until you add one.
              </p>
            )}
            {created.screening && (
              <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-900">
                <Info size={14} className="mt-0.5 shrink-0" aria-hidden="true" />NDIS screening: {created.screening}
              </p>
            )}
            {created.problems.length > 0 && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-800" role="alert">
                <p className="font-medium">Some details didn't save:</p>
                <ul className="mt-1 list-disc pl-5">{created.problems.map((p) => <li key={p}>{p}</li>)}</ul>
                <p className="mt-1">Open their profile and add them again.</p>
              </div>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
}
